import { useContext, useEffect, useState, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { useService } from '@aesop-fables/containr-react';
import { useAppStorage, useObservable } from '@aesop-fables/scrinium';
import {
  DrillDownWizard,
  drillDownWizardKey,
  drillDownTierToAdminTier,
  tierOrder,
  AdminTierTypeEnum,
} from '../../services/drillDown';
import { Page, Spacer } from '../../components';
import { Grid } from '@mui/material';
import RevShareCard from '../rev-share/RevShareCard';
import PricebookRestrictionsDrawer from '../pricebook-restrictions/PricebookRestrictionsDrawer';
import AdminSettingsCard from './AdminSettingsCard';
import AdminSettingsCardSkeleton from './AdminSettingsCardSkeleton';
import { ToastbarContext } from '../../App';
import { Toast } from '../../models/Toast';
import {
  useAvailablePricebooks,
  useGroupAvailablePricebooks,
  useAllPricebookBundles,
  useTeamAvailablePricebooks,
  useTeamPricebooksInUse,
  usePricebooksInitialized,
} from '../../hooks/usePricebookOptions';
import { useOrgBundleMappings } from '../../hooks/useOrgBundleMappings';
import { useRevShareSettings, useRevShareInitialized } from '../../hooks/useRevShareSettings';
import { LicenseCompartments, licenseStorageKey } from '../../data/license';
import { LicenseAgreementData } from '../../api/apis/LicenseAgreementApi';
import { reloadRevShareCache, resetRevShareCache } from '../../data/rev-share/mutations';
import { filterOrgRevSharePricebooks } from '../../data/rev-share/helpers/revShareSettingsUtils';
import { getRestrictionMode } from '../pricebook-restrictions/helpers/pricebookRestrictionUtils';
import { Svgs } from '../../assets/svg';

interface AdminSettingsProps {
  adminTier: AdminTierTypeEnum;
}

const AdminSettings: React.FC<AdminSettingsProps> = ({ adminTier }) => {
  const appStorage = useAppStorage();
  const { setToast } = useContext(ToastbarContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [timedOut, setTimedOut] = useState(false);
  const [pricebookOptionsDrawerOpen, setPricebookOptionsDrawerOpen] = useState(false);

  const availablePricebooks = useAvailablePricebooks(adminTier);
  const groupAvailablePricebooks = useGroupAvailablePricebooks();
  const allPricebookBundles = useAllPricebookBundles();
  const teamAvailablePricebooks = useTeamAvailablePricebooks();
  const teamPricebooksInUse = useTeamPricebooksInUse();
  const pricebooksInitialized = usePricebooksInitialized(adminTier);
  const revShareSettings = useRevShareSettings(adminTier);
  const revShareInitialized = useRevShareInitialized(adminTier);

  const licenseDataCache = appStorage.retrieve<LicenseCompartments>(licenseStorageKey);
  const licenseAgreements = useObservable(
    licenseDataCache.observe$<LicenseAgreementData[]>('licenseAgreement'),
  );
  const licenseInitialized = useObservable(
    licenseDataCache.findCompartment('licenseAgreement').initialized$,
  );

  const { mappings: bundleMappings, initialized: bundleMappingsInitialized } = useOrgBundleMappings(
    adminTier,
    availablePricebooks,
    licenseAgreements ?? [],
  );

  const isOrgTier = adminTier === AdminTierTypeEnum.Organization;
  const isTeamTier = adminTier === AdminTierTypeEnum.Team;
  const isGroupTier = adminTier === AdminTierTypeEnum.Group;

  const sortedPricebooks = useMemo(() => {
    let bundles = availablePricebooks?.pricebookBundleDtos ?? [];

    if (isOrgTier) {
      bundles = filterOrgRevSharePricebooks(bundles, licenseAgreements ?? [], bundleMappings);
    }

    return [...bundles].sort((a, b) =>
      a.pricebook.pricebookName.localeCompare(b.pricebook.pricebookName),
    );
  }, [availablePricebooks, isOrgTier, licenseAgreements, bundleMappings]);

  const inUsePricebookIds = useMemo(() => {
    if (isTeamTier) {
      if (!teamPricebooksInUse) return new Set<number>();
      return new Set(teamPricebooksInUse.filter(pb => pb.inUse).map(pb => pb.pricebook.id));
    }
    if (!licenseAgreements) return new Set<number>();
    return new Set(licenseAgreements.filter(la => la.docStatus).map(la => la.pricebookId));
  }, [isTeamTier, teamPricebooksInUse, licenseAgreements]);

  const isLoading =
    !pricebooksInitialized ||
    !revShareInitialized ||
    (isOrgTier && !licenseInitialized) ||
    (isOrgTier && !bundleMappingsInitialized);
  const showLoading = isLoading && !timedOut;

  useEffect(() => {
    if (adminTier) {
      resetRevShareCache(appStorage, adminTier);
    }
  }, [adminTier, appStorage]);

  useEffect(() => {
    if (adminTier && pricebooksInitialized && !revShareInitialized) {
      reloadRevShareCache(appStorage, adminTier);
    }
  }, [adminTier, pricebooksInitialized, revShareInitialized, appStorage]);

  useEffect(() => {
    if (isLoading && !timedOut) {
      const timeout = setTimeout(() => {
        setTimedOut(true);
        setToast(
          new Toast({
            severity: 'error',
            open: true,
            message: `Unable to load settings`,
            autoHideDuration: 3000,
          }),
        );
      }, 5000);
      return () => clearTimeout(timeout);
    }
  }, [isLoading, timedOut, setToast]);

  useEffect(() => {
    if (!isLoading && timedOut) {
      setTimedOut(false);
    }
  }, [isLoading, timedOut]);

  const showPricebookRestrictions = !isGroupTier;

  const pricebookCount =
    availablePricebooks?.pricebookBundleDtos?.filter(pb => !pb.pricebook.addOn).length ?? 0;
  const parentAvailablePricebooks = isTeamTier
    ? groupAvailablePricebooks
    : isOrgTier
      ? teamAvailablePricebooks
      : undefined;
  const restrictionMode = availablePricebooks
    ? getRestrictionMode(adminTier, availablePricebooks, parentAvailablePricebooks)
    : undefined;
  const pricebookRestrictionSummary = restrictionMode
    ? `${restrictionMode === 'inherit' ? 'Inherited' : 'Custom'} | ${pricebookCount} pricebook${pricebookCount !== 1 ? 's' : ''}`
    : '';

  const onFeeSharePage = location.pathname.includes('/fee-share');

  if (isGroupTier && !onFeeSharePage) {
    navigate('fee-share', { replace: true });
    return null;
  }

  if (onFeeSharePage) {
    return (
      <Page title='Fee share'>
        <Spacer height='xs' />
        <RevShareCard
          loading={showLoading}
          timedOut={timedOut}
          adminTier={adminTier}
          availablePricebooks={sortedPricebooks}
          parentAvailablePricebooks={parentAvailablePricebooks}
          revShareSettings={revShareSettings}
        />
      </Page>
    );
  }

  return (
    <Page title={`${adminTier} settings`}>
      <Spacer height='xs' />
      {showLoading ? (
        <AdminSettingsCardSkeleton />
      ) : (
        <Grid container spacing={{ xs: 3, lg: 4, xl: 6 }}>
          {showPricebookRestrictions && (
            <Grid size={{ xs: 12, md: 6 }}>
              <AdminSettingsCard
                icon={<Svgs.BulletLock style={{ width: 50, height: 50 }} />}
                title='Pricebook options'
                subtitle={pricebookRestrictionSummary}
                onEdit={() => setPricebookOptionsDrawerOpen(true)}
              />
            </Grid>
          )}
          <Grid size={{ xs: 12, md: 6 }}>
            <AdminSettingsCard
              icon={<Svgs.BulletLoans style={{ width: 50, height: 50 }} />}
              title='Fee share'
              subtitle='Set fee share for pricebooks.'
              onEdit={() => navigate('fee-share')}
            />
          </Grid>
        </Grid>
      )}

      {showPricebookRestrictions && (
        <PricebookRestrictionsDrawer
          drawerOpen={pricebookOptionsDrawerOpen}
          setDrawerOpen={setPricebookOptionsDrawerOpen}
          adminTier={adminTier}
          availablePricebooks={availablePricebooks}
          parentAvailablePricebooks={parentAvailablePricebooks}
          allPricebookBundles={allPricebookBundles}
          inUsePricebookIds={inUsePricebookIds}
        />
      )}
    </Page>
  );
};

const AdminSettingsWrapper = () => {
  const drillDownWizard = useService<DrillDownWizard>(drillDownWizardKey);
  const drillDownInitialized = useObservable(drillDownWizard.initialized$);
  const tier = useObservable(drillDownWizard.tier$);
  const highestRevenueAccess = useObservable(drillDownWizard.highestRevenueAccess$);
  const adminTier = tier ? drillDownTierToAdminTier(tier) : undefined;

  if (!adminTier || !drillDownInitialized) return null;

  const hasRevenueAccess =
    highestRevenueAccess !== undefined &&
    tierOrder.indexOf(highestRevenueAccess) <= tierOrder.indexOf(adminTier);

  if (!hasRevenueAccess) return null;

  return <AdminSettings adminTier={adminTier} />;
};

export default AdminSettingsWrapper;
